import type {VideoHTMLAttributes} from 'react';

import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import {usePayloadVideo} from './usePayloadVideo';

import type {Media} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'PayloadVideo'
     */
    testId?: string;
    /**
     * The video source, which may be a fully populated `Media` object, a string ID, or `null`/`undefined`.
     * Used by the `usePayloadVideo` hook to resolve responsive video data as source.
     */
    video: Media | string | null | undefined;
}

/**
 * Type definition for the `Video` component, extending the standard `VideoHTMLAttributes`
 * and omitting the default `src` attribute, as it is overridden by the custom `src` object
 * for responsive video handling.
 */
type VideoProps = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> & ComponentProps;

/**
 * A component that renders a video element with multiple source formats based on the provided video data.
 * It uses the `usePayloadVideo` hook to get the sources and renders them within a `Wrapper` component.
 * The component also supports custom video properties passed via `videoProps`.
 *
 * @param props            The component props.
 * @param props.className  A CSS class to apply to the wrapper (optional).
 * @param props.testId     A unique test identifier for the rendered component (defaults to 'PayloadVideo').
 * @param props.video      The video data containing the sources to be rendered.
 * @param props.videoProps Additional video element properties to be spread onto the wrapper component.
 * @returns                   A JSX element that renders a video wrapper with source elements.
 *
 * @example
 * ```tsx
 * <PayloadVideo video={videoData} videoProps={{ controls: true }} />
 * ```
 */
const PayloadVideo = observer(({className, testId = 'PayloadVideo', video, ...videoProps}: VideoProps) => {
    const {sources} = usePayloadVideo(video);

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Wrapper key={sources[0]?.src ?? ''} className={className} data-cy={testId} {...videoProps}>
            {sources.map(source => (
                <source
                    key={source.src}
                    media={source.media}
                    src={source.src}
                    type={source.type}
                />
            ))}
        </Wrapper>
    );
});

PayloadVideo.displayName = 'PayloadVideo';

export {PayloadVideo};

const Wrapper = styled.video``;